import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FaTimes,
  FaCalendarAlt,
  FaClock,
  FaFire,
  FaFilter,
  FaList,
  FaChartPie,
} from "react-icons/fa";
import BtnLoader from "Components/Common/Loader/BtnLoader";

const activityIcons = {
  gang_create: "🏠",
  gang_duration: "⏱️",
  individual_game: "🎮",
  gift_collected: "🎁",
  gift_sent: "💝",
  audio_call: "📞",
  video_call: "📹",
  daily_login: "✅",
};

const activityColors = {
  gang_create: "from-purple-500 to-indigo-500",
  gang_duration: "from-blue-500 to-cyan-500",
  individual_game: "from-green-500 to-emerald-500",
  gift_collected: "from-pink-500 to-rose-500",
  gift_sent: "from-red-500 to-orange-500",
  audio_call: "from-teal-500 to-green-500",
  video_call: "from-violet-500 to-purple-500",
  daily_login: "from-amber-500 to-yellow-500",
};

const activityLabels = {
  gang_create: "Gang Created",
  gang_duration: "Gang Duration",
  individual_game: "Games Played",
  gift_collected: "Gifts Received",
  gift_sent: "Gifts Sent",
  audio_call: "Audio Calls",
  video_call: "Video Calls",
  daily_login: "Daily Login",
};

const UserPointsDetailModal = ({ open, onClose, eventId, userId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview"); // "overview" | "logs"
  const [expandedDay, setExpandedDay] = useState(null);

  // Point logs state
  const [logs, setLogs] = useState([]);
  const [logsSummary, setLogsSummary] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsFilter, setLogsFilter] = useState("");
  const [logsPagination, setLogsPagination] = useState({
    page: 1,
    limit: 30,
    total: 0,
    totalPages: 0,
  });

  useEffect(() => {
    if (open && eventId && userId) {
      fetchDetails();
      fetchPointLogs(1);
    }
  }, [open, eventId, userId]);

  useEffect(() => {
    if (open && eventId && userId) {
      fetchPointLogs(1);
    }
  }, [logsFilter]);

  const fetchDetails = () => {
    setLoading(true);
    setError(null);
    axios
      .get(
        `${process.env.REACT_APP_API_URL}/event/${eventId}/user/${userId}/points-detail`
      )
      .then((res) => {
        if (res.data.success) setData(res.data.data);
        else setError(res.data.message || "Failed to fetch details.");
      })
      .catch((err) => {
        setError(
          err.response?.data?.message || "Failed to fetch user details."
        );
      })
      .finally(() => setLoading(false));
  };

  const fetchPointLogs = (page = 1) => {
    setLogsLoading(true);
    const params = { page, limit: logsPagination.limit };
    if (logsFilter) params.activityType = logsFilter;

    axios
      .get(
        `${process.env.REACT_APP_API_URL}/event/${eventId}/user/${userId}/point-logs`,
        { params }
      )
      .then((res) => {
        if (res.data.success) {
          setLogs(res.data.data.logs || []);
          setLogsSummary(res.data.data.summary || []);
          setLogsPagination(res.data.data.pagination || {});
        }
      })
      .catch(() => {})
      .finally(() => setLogsLoading(false));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 border-b bg-gradient-to-r from-[#c51369] to-[#e91e8c] px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">
                📊 User Event Points Detail
              </h2>
              {data?.event && (
                <p className="mt-1 text-sm text-white/80">
                  {data.event.name} •{" "}
                  <span className="capitalize">{data.event.status}</span>
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="rounded-full bg-white/20 p-2 text-white transition hover:bg-white/30"
            >
              <FaTimes />
            </button>
          </div>

          {/* Tabs */}
          <div className="mt-3 flex gap-2">
            <button
              onClick={() => setActiveTab("overview")}
              className={`flex items-center gap-1 rounded-lg px-4 py-1.5 text-sm font-medium transition ${
                activeTab === "overview"
                  ? "bg-white text-[#c51369]"
                  : "bg-white/20 text-white hover:bg-white/30"
              }`}
            >
              <FaChartPie className="text-xs" /> Overview
            </button>
            <button
              onClick={() => setActiveTab("logs")}
              className={`flex items-center gap-1 rounded-lg px-4 py-1.5 text-sm font-medium transition ${
                activeTab === "logs"
                  ? "bg-white text-[#c51369]"
                  : "bg-white/20 text-white hover:bg-white/30"
              }`}
            >
              <FaList className="text-xs" /> Point Logs
              {logsPagination.total > 0 && (
                <span className="ml-1 rounded-full bg-white/30 px-1.5 text-xs">
                  {logsPagination.total}
                </span>
              )}
            </button>
          </div>
        </div>

        {loading && activeTab === "overview" ? (
          <div className="flex items-center justify-center py-20">
            <BtnLoader />
            <span className="ml-3 text-gray-500">Loading user details...</span>
          </div>
        ) : error ? (
          <div className="py-20 text-center">
            <p className="text-lg text-red-500">{error}</p>
            <button
              onClick={fetchDetails}
              className="mt-4 rounded-lg bg-[#c51369] px-4 py-2 text-sm text-white hover:bg-[#a0104f]"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="p-6">
            {/* ─── OVERVIEW TAB ─── */}
            {activeTab === "overview" && data && (
              <>
                {/* User Info */}
                <div className="mb-6 flex items-center gap-4 rounded-xl bg-gradient-to-r from-gray-50 to-gray-100 p-4">
                  {data.user.avatar ? (
                    <img
                      src={data.user.avatar}
                      alt=""
                      className="h-14 w-14 rounded-full border-2 border-[#c51369] object-cover"
                    />
                  ) : (
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#c51369] text-lg font-bold text-white">
                      {data.user.nickName?.[0] || "?"}
                    </div>
                  )}
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-800">
                      {data.user.nickName || "Unknown"}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {data.user.phoneNumber || data.user.phone || "No phone"} •{" "}
                      {data.user.gender || "N/A"}
                    </p>
                    <p className="text-xs text-gray-400">
                      ID: {data.user._id}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-black text-[#c51369]">
                      {data.summary.totalPoints}
                    </p>
                    <p className="text-xs text-gray-500">Total Points</p>
                  </div>
                </div>

                {/* Summary Cards */}
                <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
                  <div className="rounded-lg bg-purple-50 p-3 text-center">
                    <p className="text-xl font-bold text-purple-600">
                      #{data.summary.rank || "-"}
                    </p>
                    <p className="text-xs text-gray-500">Rank</p>
                  </div>
                  <div className="rounded-lg bg-green-50 p-3 text-center">
                    <p className="text-xl font-bold text-green-600">
                      {data.summary.daysActive}
                    </p>
                    <p className="text-xs text-gray-500">Days Active</p>
                  </div>
                  <div className="rounded-lg bg-amber-50 p-3 text-center">
                    <p className="text-xl font-bold text-amber-600">
                      🔥 {data.summary.loginStreak}
                    </p>
                    <p className="text-xs text-gray-500">Login Streak</p>
                  </div>
                  <div className="rounded-lg bg-blue-50 p-3 text-center">
                    <p className="text-xl font-bold text-blue-600">
                      {formatDate(data.summary.lastActivityAt)}
                    </p>
                    <p className="text-xs text-gray-500">Last Active</p>
                  </div>
                </div>

                {/* Points Breakdown */}
                <div className="mb-6">
                  <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-500">
                    Points Breakdown by Activity
                  </h4>
                  <div className="space-y-2">
                    {data.pointsBreakdown
                      .sort((a, b) => b.pointsEarned - a.pointsEarned)
                      .map((item) => (
                        <div
                          key={item.activityType}
                          className="group cursor-pointer rounded-lg border border-gray-100 p-3 transition hover:shadow-md"
                          onClick={() => {
                            setLogsFilter(item.activityType);
                            setActiveTab("logs");
                          }}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">
                              {activityIcons[item.activityType] || "📌"}
                            </span>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <p className="font-semibold text-gray-800">
                                  {item.label}
                                  <span className="ml-2 text-xs text-blue-400 opacity-0 transition group-hover:opacity-100">
                                    Click to view logs →
                                  </span>
                                </p>
                                <div className="text-right">
                                  <span className="text-lg font-bold text-[#c51369]">
                                    {item.pointsEarned}
                                  </span>
                                  <span className="ml-1 text-xs text-gray-400">
                                    pts
                                  </span>
                                  {item.percentageOfTotal > 0 && (
                                    <span className="ml-2 text-xs text-gray-400">
                                      ({item.percentageOfTotal}%)
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-100">
                                <div
                                  className={`h-full rounded-full bg-gradient-to-r ${
                                    activityColors[item.activityType] ||
                                    "from-gray-400 to-gray-500"
                                  }`}
                                  style={{
                                    width: `${Math.min(
                                      item.percentageOfTotal,
                                      100
                                    )}%`,
                                  }}
                                />
                              </div>
                              <p className="mt-1 text-xs text-gray-400">
                                {item.description}
                              </p>
                              {item.criteriaConfig && (
                                <p className="mt-0.5 text-xs text-gray-400">
                                  Config: {item.criteriaConfig.pointsPerUnit}{" "}
                                  pts / {item.criteriaConfig.unit} •{" "}
                                  {item.criteriaConfig.isActive ? (
                                    <span className="text-green-500">
                                      Active
                                    </span>
                                  ) : (
                                    <span className="text-red-500">
                                      Inactive
                                    </span>
                                  )}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Peak Hours Info */}
                {data.peakHourInfo?.length > 0 && (
                  <div className="mb-6">
                    <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-500">
                      <FaFire className="mr-1 inline text-orange-500" />
                      Peak Hour Multipliers
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {data.peakHourInfo.map((ph, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1 rounded-full bg-orange-50 px-3 py-1 text-sm"
                        >
                          <FaClock className="text-orange-400" />
                          {ph.window}{" "}
                          <span className="font-bold text-orange-600">
                            {ph.multiplier}
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ─── POINT LOGS TAB ─── */}
            {activeTab === "logs" && (
              <>
                {/* Filter */}
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <FaFilter className="text-gray-400" />
                  <button
                    onClick={() => setLogsFilter("")}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                      logsFilter === ""
                        ? "bg-[#c51369] text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    All
                  </button>
                  {Object.entries(activityLabels).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() =>
                        setLogsFilter(logsFilter === key ? "" : key)
                      }
                      className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                        logsFilter === key
                          ? "bg-[#c51369] text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      {activityIcons[key]} {label}
                    </button>
                  ))}
                </div>

                {/* Log summary chips */}
                {logsSummary.length > 0 && !logsFilter && (
                  <div className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-4">
                    {logsSummary.map((s) => (
                      <div
                        key={s._id}
                        className="rounded-lg border border-gray-100 p-2 text-center"
                      >
                        <p className="text-lg">
                          {activityIcons[s._id] || "📌"}
                        </p>
                        <p className="text-sm font-bold text-[#c51369]">
                          {s.totalPoints} pts
                        </p>
                        <p className="text-xs text-gray-400">
                          {s.count} entries
                          {s.peakHourCount > 0 && (
                            <span className="ml-1 text-orange-500">
                              🔥{s.peakHourCount}
                            </span>
                          )}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Logs Table */}
                {logsLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <BtnLoader />
                    <span className="ml-3 text-gray-500">
                      Loading point logs...
                    </span>
                  </div>
                ) : logs.length === 0 ? (
                  <div className="py-16 text-center">
                    <p className="text-lg text-gray-400">
                      No point logs found.
                    </p>
                    <p className="text-sm text-gray-300">
                      Logs will appear here for future activities after
                      deployment.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-lg border">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                        <tr>
                          <th className="px-3 py-2">Time</th>
                          <th className="px-3 py-2">Activity</th>
                          <th className="px-3 py-2">Raw Count</th>
                          <th className="px-3 py-2 text-center">Base Pts</th>
                          <th className="px-3 py-2 text-center">Multiplier</th>
                          <th className="px-3 py-2 text-center">
                            Final Pts
                          </th>
                          <th className="px-3 py-2">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logs.map((log) => (
                          <tr
                            key={log._id}
                            className={`border-t transition hover:bg-gray-50 ${
                              log.isPeakHour ? "bg-orange-50/30" : ""
                            }`}
                          >
                            <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500">
                              {formatDateTime(log.createdAt)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2">
                              <span className="mr-1">
                                {activityIcons[log.activityType] || "📌"}
                              </span>
                              <span className="text-xs font-medium">
                                {activityLabels[log.activityType] ||
                                  log.activityType}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-600">
                              {typeof log.rawCount === "number"
                                ? log.rawCount % 1 !== 0
                                  ? log.rawCount.toFixed(4)
                                  : log.rawCount
                                : log.rawCount}
                            </td>
                            <td className="px-3 py-2 text-center text-xs text-gray-600">
                              {log.basePoints}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {log.isPeakHour ? (
                                <span className="inline-flex items-center gap-0.5 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-bold text-orange-600">
                                  🔥 {log.multiplier}x
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">
                                  1x
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <span className="font-bold text-[#c51369]">
                                +{log.pointsEarned}
                              </span>
                            </td>
                            <td className="max-w-[200px] truncate px-3 py-2 text-xs text-gray-500">
                              {log.reason}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                {logsPagination.totalPages > 1 && (
                  <div className="mt-4 flex items-center justify-center gap-2">
                    <button
                      disabled={logsPagination.page <= 1}
                      onClick={() =>
                        fetchPointLogs(logsPagination.page - 1)
                      }
                      className="rounded bg-gray-200 px-3 py-1 text-sm hover:bg-gray-300 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-600">
                      Page {logsPagination.page} of{" "}
                      {logsPagination.totalPages} ({logsPagination.total}{" "}
                      entries)
                    </span>
                    <button
                      disabled={
                        logsPagination.page >= logsPagination.totalPages
                      }
                      onClick={() =>
                        fetchPointLogs(logsPagination.page + 1)
                      }
                      className="rounded bg-gray-200 px-3 py-1 text-sm hover:bg-gray-300 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserPointsDetailModal;
